import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { money, date } from '../lib/format.js';

const TYPE_LABEL = { INVOICE: 'Invoice', QUOTE: 'Quote', PROFORMA: 'Proforma' };

export default function DocumentBuilder() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id;
  const type = sp.get('type') || 'INVOICE';

  const { data: clients } = useQuery({ queryKey: ['clients-all'], queryFn: () => api('/api/clients') });
  const { data: articles } = useQuery({ queryKey: ['articles-all'], queryFn: () => api('/api/articles') });
  const { data: vats } = useQuery({ queryKey: ['vat'], queryFn: () => api('/api/vat') });
  const { data: currencies } = useQuery({ queryKey: ['currencies'], queryFn: () => api('/api/currencies') });
  const { data: existing } = useQuery({ queryKey: ['document', id], queryFn: () => api(`/api/documents/${id}`), enabled: !!id });

  const [form, setForm] = useState({
    type,
    clientId: '',
    currencyCode: 'EUR',
    exchangeRate: 1,
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    language: 'en',
    notes: '',
    terms: '',
    items: [emptyItem()],
  });

  useEffect(() => {
    if (existing?.document) {
      const d = existing.document;
      setForm({
        type: d.type,
        clientId: d.clientId,
        currencyCode: d.currencyCode,
        exchangeRate: Number(d.exchangeRate),
        issueDate: date(d.issueDate),
        dueDate: date(d.dueDate),
        language: d.language,
        notes: d.notes || '',
        terms: d.terms || '',
        items: d.items.map(it => ({
          articleId: it.articleId || '', sku: it.sku || '', name: it.name, description: it.description || '',
          qty: Number(it.qty), unit: it.unit, unitPrice: Number(it.unitPrice),
          discountPct: Number(it.discountPct), vatRateId: it.vatRateId || '',
          vatPct: Number(it.vatPct), position: it.position,
        })),
      });
    }
  }, [existing]);

  const readOnly = existing?.document?.status && existing.document.status !== 'DRAFT';

  const totals = useMemo(() => {
    let sub = 0, disc = 0, vat = 0, tot = 0;
    for (const it of form.items) {
      const gross = Number(it.qty || 0) * Number(it.unitPrice || 0);
      const lineDisc = gross * (Number(it.discountPct || 0) / 100);
      const net = gross - lineDisc;
      const lineVat = net * (Number(it.vatPct || 0) / 100);
      sub += gross; disc += lineDisc; vat += lineVat; tot += net + lineVat;
    }
    return { subtotal: sub, discountTotal: disc, vatTotal: vat, total: tot };
  }, [form.items]);

  function emptyItem() {
    return { articleId: '', sku: '', name: '', description: '', qty: 1, unit: 'pcs', unitPrice: 0, discountPct: 0, vatRateId: '', vatPct: 0, position: 0 };
  }
  const setItem = (i, patch) => setForm(f => {
    const items = [...f.items]; items[i] = { ...items[i], ...patch }; return { ...f, items };
  });
  const pickArticle = (i, articleId) => {
    const a = articles?.articles?.find(x => x.id === articleId);
    if (!a) return;
    setItem(i, {
      articleId: a.id, sku: a.sku, name: a.name, description: a.description || '',
      unit: a.unit, unitPrice: Number(a.price), vatRateId: a.vatRateId || '',
      vatPct: a.vatRate ? Number(a.vatRate.rate) : 0,
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        items: form.items.map((it, i) => ({
          articleId: it.articleId || null, sku: it.sku || null, name: it.name,
          description: it.description || null, qty: Number(it.qty),
          unit: it.unit, unitPrice: Number(it.unitPrice), discountPct: Number(it.discountPct),
          vatRateId: it.vatRateId || null, vatPct: Number(it.vatPct), position: i,
        })),
      };
      return isNew
        ? api('/api/documents', { method: 'POST', body })
        : api(`/api/documents/${id}`, { method: 'PATCH', body });
    },
    onSuccess: (r) => nav(`/documents/${r.document.id}`),
  });

  const client = clients?.clients?.find(c => c.id === form.clientId);

  return (
    <>
      <div className="page-header">
        <h1>{isNew ? `New ${TYPE_LABEL[type]}` : `Edit ${existing?.document?.fullNumber || ''}`}</h1>
        <button className="btn btn-secondary" onClick={() => nav(-1)}>Back</button>
      </div>

      <div className="card">
        <div className="row">
          <div className="field">
            <label>Client</label>
            <select value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value, language: clients?.clients?.find(c => c.id === e.target.value)?.defaultLang || f.language }))} disabled={readOnly}>
              <option value="">—</option>
              {clients?.clients?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Issue date</label><input type="date" value={form.issueDate} onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))} disabled={readOnly} /></div>
          <div className="field"><label>Due date</label><input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} disabled={readOnly} /></div>
          <div className="field"><label>Currency</label>
            <select value={form.currencyCode} onChange={e => setForm(f => ({ ...f, currencyCode: e.target.value }))} disabled={readOnly}>
              {currencies?.currencies?.map(c => <option key={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div className="field"><label>FX rate</label><input type="number" step="0.000001" value={form.exchangeRate} onChange={e => setForm(f => ({ ...f, exchangeRate: Number(e.target.value) }))} disabled={readOnly} /></div>
          <div className="field"><label>Language</label>
            <select value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} disabled={readOnly}>
              <option value="en">EN</option><option value="sq">SQ</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Line items</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Article / SKU</th><th>Name</th>
              <th style={{ width: 80 }}>Qty</th><th style={{ width: 80 }}>Unit</th>
              <th style={{ width: 110 }}>Price</th><th style={{ width: 90 }}>Disc %</th>
              <th style={{ width: 90 }}>VAT %</th><th style={{ width: 110, textAlign: 'right' }}>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => {
              const gross = Number(it.qty || 0) * Number(it.unitPrice || 0);
              const net = gross * (1 - Number(it.discountPct || 0) / 100);
              const total = net * (1 + Number(it.vatPct || 0) / 100);
              return (
                <tr key={i}>
                  <td>
                    <select value={it.articleId} onChange={e => pickArticle(i, e.target.value)} disabled={readOnly}>
                      <option value="">—</option>
                      {articles?.articles?.map(a => <option key={a.id} value={a.id}>{a.sku} · {a.name}</option>)}
                    </select>
                  </td>
                  <td><input value={it.name} onChange={e => setItem(i, { name: e.target.value })} disabled={readOnly} /></td>
                  <td><input type="number" step="0.01" value={it.qty} onChange={e => setItem(i, { qty: e.target.value })} disabled={readOnly} /></td>
                  <td><input value={it.unit} onChange={e => setItem(i, { unit: e.target.value })} disabled={readOnly} /></td>
                  <td><input type="number" step="0.01" value={it.unitPrice} onChange={e => setItem(i, { unitPrice: e.target.value })} disabled={readOnly} /></td>
                  <td><input type="number" step="0.01" value={it.discountPct} onChange={e => setItem(i, { discountPct: e.target.value })} disabled={readOnly} /></td>
                  <td>
                    <select value={it.vatRateId} onChange={e => {
                      const r = vats?.rates?.find(x => x.id === e.target.value);
                      setItem(i, { vatRateId: e.target.value, vatPct: r ? Number(r.rate) : 0 });
                    }} disabled={readOnly}>
                      <option value="">—</option>
                      {vats?.rates?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>{money(total, form.currencyCode)}</td>
                  <td>{!readOnly && <button className="btn btn-danger btn-sm" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}>×</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && <button className="btn btn-secondary btn-sm mt-8" onClick={() => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))}>+ Line</button>}
      </div>

      <div className="card">
        <div className="row">
          <div className="field" style={{ flex: 2 }}><label>Notes</label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} disabled={readOnly} /></div>
          <div className="field" style={{ flex: 2 }}><label>Terms</label><textarea value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} disabled={readOnly} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ background: 'var(--sky-pale)', padding: 14, borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><strong>{money(totals.subtotal, form.currencyCode)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Discount</span><strong>−{money(totals.discountTotal, form.currencyCode)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT</span><strong>{money(totals.vatTotal, form.currencyCode)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--navy-900)', marginTop: 8, paddingTop: 8, fontSize: 16, color: 'var(--navy-900)' }}>
                <span>Total</span><strong>{money(totals.total, form.currencyCode)}</strong>
              </div>
            </div>
            {!readOnly && (
              <button className="btn btn-primary mt-16" style={{ width: '100%', justifyContent: 'center' }} disabled={save.isPending || !form.clientId || form.items.length === 0} onClick={() => save.mutate()}>
                {save.isPending ? <span className="spinner" /> : 'Save draft'}
              </button>
            )}
            {readOnly && <div className="muted mt-16" style={{ textAlign: 'center' }}>This document is issued and cannot be edited.</div>}
          </div>
        </div>
      </div>
    </>
  );
}
